import { FlatList, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type {
  FeaturePageSectionViewModel,
  FeaturePageViewModel,
} from '../../types/featurePage';
import { AppButton } from '../ui/AppButton';
import { EmptyView } from '../ui/EmptyView';
import { ErrorView } from '../ui/ErrorView';
import { LoadingView } from '../ui/LoadingView';
import { FeaturePageActionList } from './FeaturePageActionList';
import { FeaturePageMetricGrid } from './FeaturePageMetricGrid';
import { FeaturePageSectionCard } from './FeaturePageSectionCard';
import { FeaturePageSummaryCard } from './FeaturePageSummaryCard';

export type FeaturePageRouteState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; data: FeaturePageViewModel };

type FeaturePageRouteLayoutProps = {
  state: FeaturePageRouteState;
  onBack: () => void;
  onRetry: () => void;
  onPressAction: (href: string | null) => void;
};

export function FeaturePageRouteLayout({
  state,
  onBack,
  onRetry,
  onPressAction,
}: FeaturePageRouteLayoutProps) {
  const renderHeader = () => {
    if (state.status !== 'success') {
      return null;
    }

    return (
      <View style={styles.headerContent}>
        <FeaturePageSummaryCard
          title={state.data.title}
          subtitle={state.data.subtitle}
          statusLabel={state.data.statusLabel}
          progressPercent={state.data.progressPercent}
        />
        <FeaturePageMetricGrid metrics={state.data.metrics} />
      </View>
    );
  };

  const renderSection = ({ item }: { item: FeaturePageSectionViewModel }) => (
    <FeaturePageSectionCard
      title={item.title}
      description={item.description}
      statusLabel={item.statusLabel}
      progressPercent={item.progressPercent}
      nextStepLabel={item.nextStepLabel}
    />
  );

  const renderFooter = () => {
    if (state.status !== 'success') {
      return null;
    }

    return (
      <View style={styles.footer}>
        <FeaturePageActionList actions={state.data.actions} onPressAction={onPressAction} />
      </View>
    );
  };

  const content = () => {
    if (state.status === 'loading') {
      return <LoadingView message="正在加载功能数据" />;
    }

    if (state.status === 'error') {
      return <ErrorView message={state.message} onRetry={onRetry} />;
    }

    if (state.status === 'empty') {
      return (
        <EmptyView
          title="暂无功能数据"
          description="当前功能还没有可展示的模块，请稍后再试。"
          actionLabel="重新加载"
          onAction={onRetry}
        />
      );
    }

    return (
      <FlatList
        data={state.data.sections}
        keyExtractor={(section) => section.id}
        renderItem={renderSection}
        ListHeaderComponent={renderHeader}
        ListFooterComponent={renderFooter}
        contentContainerStyle={styles.listContent}
      />
    );
  };

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="返回"
          variant="ghost"
          onPress={onBack}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
      </View>
      {content()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
  },
  backButton: {
    alignSelf: 'flex-start',
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  listContent: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  headerContent: {
    gap: tokens.spacing.lg,
  },
  footer: {
    paddingTop: tokens.spacing.sm,
  },
});
